import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../shared/Seo';

const PRIVACY_SECTIONS = [
  {
    title: '1. Information We Collect',
    bullets: [
      {
        text: 'Personal Information You Provide Directly:',
        subItems: [
          'Account Information: When you create an account, we collect your name, email address, and a password.',
          'Purchase Information: When you make a purchase, we collect your shipping address, phone number, and payment details. Please note that the payments are processed by a third-party payment provider, and we do not store your full credit card details on our servers.',
          'Locker System Data: We collect and store data related to the items you place in your digital Locker.',
          'Communication Information: If you contact our customer service, we collect the contents of your communication and any other information you choose to provide.',
        ],
      },
      {
        text: 'Information Collected Automatically:',
        subItems: [
          'Usage Data: We automatically collect information about your interactions with the Site, including your IP address, browser type, pages visited, time spent on each page, and the links you click.',
          'Cookies and Tracking Technologies: We use cookies, web beacons, and similar technologies to track activity on our Site and hold certain information. This helps us understand your preferences and improve your experience. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.',
        ],
      },
    ],
  },
  {
    title: '2. How We Use Your Information',
    body: 'We use the information we collect for the following purposes:',
    bullets: [
      'To Fulfill Your Orders: To process payments, arrange for shipping, and provide you with invoices and/or order confirmations.',
      'To Manage Your Account and Locker: To create and manage your account, operate the Locker System, and showcase your collectibles as you direct.',
      'To Communicate With You: To respond to your inquiries, provide customer support, and send you important administrative messages (e.g., order updates, security alerts).',
      'For Marketing (with your consent): To send you information about new products, special offers, and other updates you will find valuable. You can opt out of these communications at any time.',
      'To Improve Our Site: To analyze how users interact with our Site, troubleshoot technical problems, and develop new features and services.',
      'For Security and Fraud Prevention: To monitor and prevent fraudulent transactions or unauthorized activities, and to protect the rights and safety of our users and PonPon.',
    ],
  },
  {
    title: '3. How We Share Your Information',
    body:
      'We do not sell your personal information to third parties. We may share your information in the following limited circumstances:',
    bullets: [
      'Service Providers: We share your information with trusted third-party vendors who perform services on our behalf, such as payment processing, order fulfillment, shipping, data analysis, email delivery, and marketing support. These providers are contractually obligated to use your information only to perform their assigned services.',
      'Legal Compliance and Safety: We may disclose your information if required to do so by law or in response to a valid legal request, such as a subpoena, court order, or government investigation. We may also share information to protect the rights, property, or safety of PonPon Interactive LLC, our users, or the public.',
      'Business Transfers: In the event of a merger, acquisition, or sale of all or a portion of our assets, your information may be transferred to the new entity, provided that the receiving entity continues to handle your information in a manner consistent with this Privacy Policy.',
    ],
  },
  {
    title: '4. The Locker System and Showcase Feature',
    bullets: [
      'The contents of your Locker are stored on our secure servers.',
      'The showcase feature allows you to display your collection. It is your responsibility to manage the privacy of your showcase link. If you share the link, the contents of your Locker may be viewed by others. We are not responsible for the privacy of your Locker if you choose to share the link.',
    ],
  },
  {
    title: '5. Data Security',
    body:
      'We implement appropriate technical and organizational security measures designed to protect your personal information from unauthorized access, loss, misuse, or disclosure. However, no method of transmission over the Internet or method of electronic storage is 100% secure. While we strive to use commercially reasonable means to protect your personal information, we cannot guarantee its absolute security.',
  },
  {
    title: '6. Your Data Protection Rights',
    body: 'Depending on your location, you may have the following rights regarding your personal information:',
    bullets: [
      'Access and Portability: The right to request copies of the personal data we hold about you.',
      'Correction: The right to request that we correct or complete any inaccurate or incomplete information.',
      'Deletion: The right to request that we erase your personal data, subject to certain legal or operational conditions.',
      'Restriction of Processing: The right to request that we limit the processing of your personal data, under certain conditions.',
      'Opt-Out of Marketing: You can unsubscribe from our marketing emails at any time by clicking the "unsubscribe" link located at the bottom of every email or by contacting us directly.',
    ],
    footnote:
      'To exercise any of these rights, please contact us using the information in the "Contact Us" section below.',
  },
  {
    title: "7. Children's Privacy",
    body:
      'Our Site is not intended for individuals under the age of 13. We do not knowingly collect personal information from individuals under the age of 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us, and we will take steps to delete that information.',
  },
  {
    title: '8. Changes to This Privacy Policy',
    body:
      'We reserve the right to update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.',
  },
  {
    title: '9. Contact Us',
    body:
      'If you have any questions about this Privacy Policy or your personal data, please contact us at: salesponpon@gmail.com.',
  },
];


const PrivacyPage = () => {
  useEffect(() => {
    const smoothScroll = () => window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const ensureTop = () => {
      const doc = document?.documentElement;
      const body = document?.body;
      const current = (doc?.scrollTop || 0) + (body?.scrollTop || 0);
      if (current > 0) {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    };

    smoothScroll();
    const retryId = window.setTimeout(ensureTop, 220);
    const fallbackId = window.setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    }, 900);

    return () => {
      window.clearTimeout(retryId);
      window.clearTimeout(fallbackId);
    };
  }, []);

  return (
    <div className="flex flex-col gap-8">
      <Seo
        title="Privacy Policy | PonPon!"
        description="Learn how PonPon! collects, uses, and protects your personal information—including Locker contents, order details, and support history."
        canonical="https://ponpon.app/privacy"
        type="article"
      />
      <header>
        <h1 className="font-display text-3xl font-semibold text-ponpon-navy sm:text-4xl">
          Privacy Policy
        </h1>
        <p className="mt-2 text-xs uppercase tracking-wide text-ponpon-navy/60">
          Last Update: November, 2025
        </p>
        <div className="mt-2 space-y-3 text-sm leading-relaxed text-ponpon-navy/75">
          <p>
            This Privacy Policy describes how PonPon Interactive LLC ('we,'
            'us,' or 'our') collects, uses, and shares your personal information when you use our
            website, ponpon.app (the 'Site'), and related services.
          </p>
          <p>
            By accessing or using the Site, you agree to the collection and use of your information
            in accordance with this Privacy Policy.
          </p>
        </div>
      </header>

      <div className="space-y-6">
        {PRIVACY_SECTIONS.map(({ title, body, bullets, footnote }) => (
          <div key={title} className="space-y-2">
            <h2 className="text-base font-semibold text-ponpon-navy sm:text-lg">{title}</h2>
            {body ? <p className="text-sm leading-relaxed text-ponpon-navy/75">{body}</p> : null}
            {Array.isArray(bullets) ? (
              <ul className="list-disc space-y-2 pl-5 text-sm leading-relaxed text-ponpon-navy/75">
                {bullets.map((item, index) => (
                  <li key={`${title}-${index}`}>
                    <span className="whitespace-pre-line">
                      {typeof item === 'string' ? item : item.text}
                    </span>
                    {item && typeof item === 'object' && Array.isArray(item.subItems) ? (
                      <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-ponpon-navy/75">
                        {item.subItems.map((subItem, subIndex) => (
                          <li
                            key={`${title}-${index}-sub-${subIndex}`}
                            className="whitespace-pre-line"
                          >
                            {subItem}
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {footnote ? (
              <p className="text-sm leading-relaxed text-ponpon-navy/75">{footnote}</p>
            ) : null}
          </div>
        ))}
      </div>

      <div className="mt-4 flex justify-end">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-full bg-ponpon-coral px-6 py-2.5 text-sm font-semibold text-white shadow-[0_12px_26px_rgba(226,58,50,0.28)] transition hover:-translate-y-0.5 hover:bg-[#E23A32] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ponpon-coral/60"
        >
          <span aria-hidden="true">←</span>
          Back to homepage
        </Link>
      </div>
    </div>
  );
};

export default PrivacyPage;
